import { useNavigate, useLocation } from "react-router-dom";
import { DnaBackground } from "@/components/DnaBackground";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, ArrowLeft, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { API_URL } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";
import { useTextDirection } from "@/lib/direction";

export default function EmailVerificationSent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const direction = useTextDirection();
  const [resending, setResending] = useState(false);
  
  // Get email from navigation state
  const email = location.state?.email || '';

  const handleResendEmail = async () => {
    if (!email) {
      toast.error(t("errors.emailNotFound"));
      navigate('/auth');
      return;
    }

    setResending(true);
    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(t("emailVerification.sent"));
      } else {
        toast.error(getLocalizedErrorMessage(data.message, "errors.resendVerificationFailed"));
      }
    } catch (error) {
      toast.error(getLocalizedErrorMessage(error, "errors.generic"));
      console.error('Resend error:', error);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative" dir={direction}>
      <DnaBackground />
      
      <Card className="w-full max-w-md p-8 bg-card border-border shadow-2xl animate-fade-in">
        <div className="flex flex-col items-center text-center space-y-6">
          {/* Email Icon */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center animate-scale-in">
              <Mail className="w-12 h-12 text-primary" />
            </div>
            <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
          </div>

          {/* Heading */}
          <div className="space-y-2">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
              Check Your Email
            </h1>
            <p className="text-lg text-muted-foreground">
              We've sent a verification link to
            </p>
            {email && (
              <p className="text-lg font-semibold text-primary">
                {email}
              </p>
            )}
          </div>

          {/* Instructions */}
          <div className="space-y-3 text-start w-full bg-muted/30 p-4 rounded-lg border border-border/50">
            <p className="text-sm text-muted-foreground font-medium">
              To complete your registration:
            </p>
            <ol className="text-sm text-muted-foreground space-y-2 pl-4">
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">1.</span>
                <span>Open your email inbox</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">2.</span>
                <span>Click the verification link in the email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">3.</span>
                <span>You'll be redirected to your dashboard</span>
              </li>
            </ol>
          </div>

          {/* Didn't receive email section */}
          <div className="w-full pt-4 border-t border-border/50">
            <p className="text-sm text-muted-foreground mb-3">
              Didn't receive the email?
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleResendEmail}
                disabled={resending}
                className="w-full gap-2"
                variant="outline"
              >
                {resending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Resend Verification Email
                  </>
                )}
              </Button>
              
              <p className="text-xs text-muted-foreground mt-2">
                Check your spam folder or try resending after a minute
              </p>
            </div>
          </div>

          {/* Back button */}
          <div className="w-full pt-4">
            <Button
              onClick={() => navigate('/auth')}
              variant="ghost"
              className="w-full gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
